import { useState } from "react";
import { Stack, Checkbox, TextField, PrimaryButton } from "@fluentui/react";
import { ThumbLike20Regular, ThumbDislike20Regular, ThumbLike20Filled, ThumbDislike20Filled } from "@fluentui/react-icons";
import { Citation, FeedbackRequest, GroundednessData } from "../../api";
import { useAuthenticatedApi } from "../../api/AuthenticatedApiContext";
import styles from "./FeedbackButtons.module.css";

// Regulation-specific error categories
const ERROR_CATEGORIES = [
  { key: "outdated_regulation", label: "Outdated regulation cited" },
  { key: "wrong_regulation", label: "Wrong regulation cited" },
  { key: "missing_context", label: "Missing important context" },
  { key: "incorrect_interpretation", label: "Incorrect interpretation" },
  { key: "other", label: "Other" },
];

interface FeedbackButtonsProps {
  conversationId: string;
  messageId: string;
  question: string;
  answer: string;
  citations: Citation[];
  expandedMode?: boolean;
  groundedness?: GroundednessData | null;
}

export const FeedbackButtons = ({
  conversationId,
  messageId,
  question,
  answer,
  citations,
  expandedMode,
  groundedness,
}: FeedbackButtonsProps) => {
  const { submitFeedback } = useAuthenticatedApi();
  const [feedbackGiven, setFeedbackGiven] = useState<"positive" | "negative" | null>(null);
  const [showNegativeForm, setShowNegativeForm] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handlePositiveFeedback = async () => {
    if (submitted) return;

    setIsSubmitting(true);
    const feedbackData: FeedbackRequest = {
      conversation_id: conversationId,
      message_id: messageId,
      question,
      answer,
      citations,
      feedback_type: "positive",
      expanded_mode: expandedMode,
      groundedness: groundedness || undefined,
    };

    const success = await submitFeedback(feedbackData);
    if (success) {
      setFeedbackGiven("positive");
      setSubmitted(true);
    }
    setIsSubmitting(false);
  };

  const handleNegativeClick = () => {
    if (submitted) return;
    setFeedbackGiven("negative");
    setShowNegativeForm(true);
  };

  const handleCategoryChange = (key: string, checked: boolean) => {
    if (checked) {
      setSelectedCategories([...selectedCategories, key]);
    } else {
      setSelectedCategories(selectedCategories.filter((k) => k !== key));
    }
  };

  const handleSubmitNegativeFeedback = async () => {
    setIsSubmitting(true);
    const feedbackData: FeedbackRequest = {
      conversation_id: conversationId,
      message_id: messageId,
      question,
      answer,
      citations,
      feedback_type: "negative",
      categories: selectedCategories,
      text: feedbackText,
      expanded_mode: expandedMode,
      groundedness: groundedness || undefined,
    };

    const success = await submitFeedback(feedbackData);
    if (success) {
      setShowNegativeForm(false);
      setSubmitted(true);
    }
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <div className={styles.feedbackContainer}>
        <span className={styles.thankYouMessage}>
          {feedbackGiven === "positive" ? "Thanks for the feedback!" : "Thanks, we'll use this to improve."}
        </span>
      </div>
    );
  }

  return (
    <div className={styles.feedbackContainer}>
      <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
        <button
          className={`${styles.feedbackButton} ${feedbackGiven === "positive" ? styles.selected : ""}`}
          onClick={handlePositiveFeedback}
          disabled={isSubmitting || submitted}
          title="Good response"
          aria-label="Good response"
        >
          {feedbackGiven === "positive" ? <ThumbLike20Filled /> : <ThumbLike20Regular />}
        </button>
        <button
          className={`${styles.feedbackButton} ${feedbackGiven === "negative" ? styles.selected : ""}`}
          onClick={handleNegativeClick}
          disabled={isSubmitting || submitted}
          title="Bad response"
          aria-label="Bad response"
        >
          {feedbackGiven === "negative" ? <ThumbDislike20Filled /> : <ThumbDislike20Regular />}
        </button>
      </Stack>

      {showNegativeForm && (
        <div className={styles.negativeFeedbackForm}>
          <p className={styles.formTitle}>What was wrong with this response?</p>
          <Stack tokens={{ childrenGap: 8 }}>
            {ERROR_CATEGORIES.map((category) => (
              <Checkbox
                key={category.key}
                label={category.label}
                checked={selectedCategories.includes(category.key)}
                onChange={(_, checked) => handleCategoryChange(category.key, checked || false)}
              />
            ))}
          </Stack>
          <TextField
            label="Additional details (optional)"
            multiline
            rows={3}
            value={feedbackText}
            onChange={(_, value) => setFeedbackText(value || "")}
            placeholder="Please describe what was incorrect..."
            className={styles.textField}
          />
          <PrimaryButton
            text={isSubmitting ? "Submitting..." : "Submit Feedback"}
            onClick={handleSubmitNegativeFeedback}
            disabled={isSubmitting || selectedCategories.length === 0}
            className={styles.submitButton}
          />
        </div>
      )}
    </div>
  );
};
